import { createSignal, Show, For, onMount } from "solid-js";
import { useNavigate } from "@solidjs/router";
import { View, Text, hexColor } from "@lightningtv/solid";
import axios from "axios";
import Header from "../../components/Header";
import { fontFamily, isRTL, t } from "../../translations/configuration";
import InputField from "./InputField";
import FormatOption from "./FormatOption";
import SubmitButton from "./SubmitButton";
import { IconButton } from "../../components/playlistComponents/TopRightHeaders";
import KbPanel from "../../components/KbPanel";

const URL = "https://orbixplay-applications-backend.orbixplay.com";

const KbPanelFader = (props: {
  value: () => string;
  fieldLabel: () => string;
  onChar: (c: string) => void;
  onBackspace: () => void;
  onDone: () => void;
}) => {
  const [entered, setEntered] = createSignal(false);
  onMount(() => setEntered(true));
  return (
    <View
      alpha={entered() ? 1 : 0}
      x={entered() ? 0 : 15}
      style={{ transition: { alpha: { duration: 280 }, x: { duration: 280 } } }}
    >
      <KbPanel {...props} />
    </View>
  );
};

const AddPlaylist = () => {
  const navigate = useNavigate();
  const licenceData = JSON.parse(localStorage.getItem("licenceData") || "{}");
  const storedDeviceId = licenceData.deviceid || "—";

  const [selectedFormat, setSelectedFormat] = createSignal(0);

  const [nameVal, setNameVal] = createSignal("");
  const [urlVal, setUrlVal] = createSignal("");
  const [usernameVal, setUsernameVal] = createSignal("");
  const [passwordVal, setPasswordVal] = createSignal("");
  const [codeVal, setCodeVal] = createSignal("");

  type FieldKey = "name" | "url" | "username" | "password" | "code";
  const [kbOpen, setKbOpen] = createSignal(false);
  const [activeField, setActiveField] = createSignal<FieldKey>("name");
  const [kbLabel, setKbLabel] = createSignal("");
  let lastFieldRef: any, headerRef: any, settingRef: any;

  const [submitting, setSubmitting] = createSignal(false);
  const [submitErrors, setSubmitErrors] = createSignal<string[]>([]);
  let errorTimer: ReturnType<typeof setTimeout>;

  const showErrors = (errors: string[]) => {
    clearTimeout(errorTimer);
    setSubmitErrors(errors);
    errorTimer = setTimeout(() => setSubmitErrors([]), 3000);
  };

  const validate = (): string[] => {
    const errors: string[] = [];
    const fmt = selectedFormat();
    if (fmt !== 3 && !nameVal().trim()) errors.push(t("playlist_name_required"));
    if (fmt === 0 && !urlVal().trim()) errors.push(t("m3u_url_required"));
    if (fmt === 1 && !urlVal().trim()) errors.push(t("server_url_required"));
    if ((fmt === 1 || fmt === 2) && !usernameVal().trim()) errors.push(t("username_required"));
    if ((fmt === 1 || fmt === 2) && !passwordVal().trim()) errors.push(t("password_required"));
    if (fmt === 2 && !codeVal().trim()) errors.push(t("orbix_code_required"));
    if (fmt === 3 && !codeVal().trim()) errors.push(t("list_code_required"));
    return errors;
  };

  const getVal = (): string => {
    const k = activeField();
    if (k === "name") return nameVal();
    if (k === "url") return urlVal();
    if (k === "username") return usernameVal();
    if (k === "password") return passwordVal();
    return codeVal();
  };

  const appendChar = (c: string) => {
    const k = activeField();
    if (k === "name") setNameVal(v => v + c);
    else if (k === "url") setUrlVal(v => v + c);
    else if (k === "username") setUsernameVal(v => v + c);
    else if (k === "password") setPasswordVal(v => v + c);
    else setCodeVal(v => v + c);
  };

  const deleteChar = () => {
    const k = activeField();
    if (k === "name") setNameVal(v => v.slice(0, -1));
    else if (k === "url") setUrlVal(v => v.slice(0, -1));
    else if (k === "username") setUsernameVal(v => v.slice(0, -1));
    else if (k === "password") setPasswordVal(v => v.slice(0, -1));
    else setCodeVal(v => v.slice(0, -1));
  };

  const openKb = (key: FieldKey, label: string, fieldRef: any) => {
    setActiveField(key);
    setKbLabel(label);
    lastFieldRef = fieldRef;
    setKbOpen(true);
  };

  const closeKb = () => {
    setKbOpen(false);
    setTimeout(() => lastFieldRef?.setFocus(), 50);
  };

  let fmt0Ref: any, fmt1Ref: any, fmt2Ref: any, fmt3Ref: any;
  let nameRef: any, urlRef: any, usernameRef: any, passwordRef: any, codeRef: any, submitRef: any;

  const fmtRef = (i: number) => [fmt0Ref, fmt1Ref, fmt2Ref, fmt3Ref][i];
  const firstField = () => (selectedFormat() <= 1 ? urlRef : codeRef);
  const lastField = () => (selectedFormat() === 1 || selectedFormat() === 2 ? passwordRef : firstField());

  const FORMAT_KEYS = ["m3u", "usernamepass", "orbixcode", "listcode"];

  const handleSubmit = async () => {
    if (submitting()) return;
    setUrlVal(v => v.replace(/\s/g, ""));
    setUsernameVal(v => v.replace(/\s/g, ""));
    setPasswordVal(v => v.replace(/\s/g, ""));
    setCodeVal(v => v.replace(/\s/g, ""));
    const errors = validate();
    if (errors.length > 0) { showErrors(errors); return; }
    setSubmitErrors([]);
    setSubmitting(true);
    try {
      const loginRes = await axios.post(`${URL}/api/auth/login`, { deviceid: storedDeviceId });
      const token: string = loginRes.data?.access_token;
      if (!token) throw new Error("Login failed — no token received");

      const fmt = FORMAT_KEYS[selectedFormat()];
      const payload: Record<string, any> = {
        deviceid: storedDeviceId,
        playlistFormat: fmt,
        ...(selectedFormat() !== 3 && { playlistName: nameVal() }),
      };

      if (fmt === "m3u") {
        payload.m3uUrl = urlVal();
      } else if (fmt === "usernamepass") {
        payload.url = urlVal();
        payload.username = usernameVal();
        payload.password = passwordVal();
      } else if (fmt === "orbixcode") {
        payload.host_code = Number(codeVal());
        payload.username = usernameVal();
        payload.password = passwordVal();
      } else {
        payload.listcode = Number(codeVal());
      }

      await axios.post(`${URL}/api/auth/create_tv_playlist`, payload, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });

      sessionStorage.setItem("justAddedPlaylist", "1");
      navigate("/");
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Failed to add playlist";
      showErrors([msg]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <View color={hexColor("#0d0f14")} onEscape={() => navigate("/")}>
      <Header screen={t("add_playlist_screen")} ref={(el) => { headerRef = el; }} onDown={() => fmt0Ref?.setFocus()} onRight={() => settingRef?.setFocus()} />

      <View x={isRTL() ? 50 : 1700} y={50}>
        {/* Settings button */}
        <IconButton
          onRef={(el) => { settingRef = el; }}
          x={100} y={0}
          src="https://i.ibb.co/Jjp8bmn4/setting.png"
          onLeft={() => headerRef?.setFocus()}
          onDown={() => fmt0Ref?.setFocus()}
          onEnter={() => navigate("/userInfo")}
        />
      </View>

      <View x={150} y={160} width={1620} height={860} borderRadius={20} color={hexColor("#15171b")}>

        {/* ── LEFT PANEL — format selector  OR  keyboard ── */}
        <View x={60} y={60} width={490}>

          {/* Format options — always rendered, slides left + fades out when kb opens */}
          <View
            alpha={kbOpen() ? 0 : 1}
            x={kbOpen() ? -15 : 0}
            style={{ transition: { alpha: { duration: 280 }, x: { duration: 280 } } }}
          >
            <Text style={{ fontSize: 30, color: hexColor("#8b949e"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>
              {t("device_id")}
            </Text>
            <Text x={0} y={45} style={{ fontSize: 30, color: hexColor("#326dcf"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>
              {storedDeviceId}
            </Text>

            <Text x={0} y={125} style={{ fontSize: 25, color: hexColor("#8b949e"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>
              {t("playlist_format")}
            </Text>

            <FormatOption ref={(el) => { fmt0Ref = el; }} y={165}
              label={t("m3u")} selected={selectedFormat() === 0}
              onEnter={() => setSelectedFormat(0)}
              onUp={() => headerRef?.setFocus()}
              onDown={() => fmt1Ref?.setFocus()}
              onRight={() => selectedFormat() === 3 ? codeRef?.setFocus() : nameRef?.setFocus()}
            />
            <FormatOption ref={(el) => { fmt1Ref = el; }} y={245}
              label={t("username_password")} selected={selectedFormat() === 1}
              onEnter={() => setSelectedFormat(1)}
              onUp={() => fmt0Ref?.setFocus()}
              onDown={() => fmt2Ref?.setFocus()}
              onRight={() => selectedFormat() === 3 ? codeRef?.setFocus() : nameRef?.setFocus()}
            />
            <FormatOption ref={(el) => { fmt2Ref = el; }} y={325}
              label={t("orbix_code")} selected={selectedFormat() === 2}
              onEnter={() => setSelectedFormat(2)}
              onUp={() => fmt1Ref?.setFocus()}
              onDown={() => fmt3Ref?.setFocus()}
              onRight={() => selectedFormat() === 3 ? codeRef?.setFocus() : nameRef?.setFocus()}
            />
            <FormatOption ref={(el) => { fmt3Ref = el; }} y={405}
              label={t("list_code")} selected={selectedFormat() === 3}
              onEnter={() => setSelectedFormat(3)}
              onUp={() => fmt2Ref?.setFocus()}
              onRight={() => codeRef?.setFocus()}
            />
          </View>

          {/* Keyboard — mounted on open, slides in + fades in from right */}
          <Show when={kbOpen()}>
            <KbPanelFader
              value={() => getVal()}
              fieldLabel={() => kbLabel()}
              onChar={appendChar}
              onBackspace={deleteChar}
              onDone={closeKb}
            />
          </Show>

        </View>

        {/* ── DIVIDER ── */}
        <View x={606} y={40} width={2} height={780} color={hexColor("#2d3748")} />

        {/* ── RIGHT PANEL — input fields ── */}
        <View x={646} y={60} width={920}>
          <Show when={selectedFormat() !== 3}>
            <Text style={{ fontSize: 19, color: hexColor("#8b949e"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>
              {t("playListName")}
            </Text>
            <InputField
              ref={(el) => { nameRef = el; }} y={32} autofocus={selectedFormat() !== 3}
              placeholder={t("my_playlist")} value={nameVal()}
              onEnter={() => openKb("name", "Playlist Name", nameRef)}
              onUp={() => settingRef?.setFocus()}
              onDown={() => firstField()?.setFocus()}
              onLeft={() => fmtRef(selectedFormat())?.setFocus()}
            />
          </Show>

          {/* M3U */}
          <Show when={selectedFormat() === 0}>
            <Text x={0} y={128} style={{ fontSize: 19, color: hexColor("#8b949e"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>
              {t("m3uUrl")}
            </Text>
            <InputField ref={(el) => { urlRef = el; }} y={160}
              placeholder="http://example.com/playlist.m3u" value={urlVal()}
              onEnter={() => openKb("url", "M3U Playlist URL", urlRef)}
              onUp={() => nameRef?.setFocus()}
              onDown={() => submitRef?.setFocus()}
              onLeft={() => fmt0Ref?.setFocus()}
            />
          </Show>

          {/* Username / Password */}
          <Show when={selectedFormat() === 1}>
            <Text x={0} y={128} style={{ fontSize: 19, color: hexColor("#8b949e"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>
              {t("server_URL")}
            </Text>
            <InputField ref={(el) => { urlRef = el; }} y={160}
              placeholder="http://example.com" value={urlVal()}
              onEnter={() => openKb("url", "Server URL", urlRef)}
              onUp={() => nameRef?.setFocus()}
              onDown={() => usernameRef?.setFocus()}
              onLeft={() => fmt1Ref?.setFocus()}
            />
            <Text x={0} y={256} style={{ fontSize: 19, color: hexColor("#8b949e"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>
              {t("username_req")}
            </Text>
            <InputField ref={(el) => { usernameRef = el; }} y={288}
              placeholder={t("username_placeholder")} value={usernameVal()}
              onEnter={() => openKb("username", "Username", usernameRef)}
              onUp={() => urlRef?.setFocus()}
              onDown={() => passwordRef?.setFocus()}
              onLeft={() => fmt1Ref?.setFocus()}
            />
            <Text x={0} y={384} style={{ fontSize: 19, color: hexColor("#8b949e"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>
              {t("password_req")}
            </Text>
            <InputField ref={(el) => { passwordRef = el; }} y={416}
              placeholder={t("password_placeholder")}
              value={passwordVal() ? "•".repeat(passwordVal().length) : ""}
              onEnter={() => openKb("password", "Password", passwordRef)}
              onUp={() => usernameRef?.setFocus()}
              onDown={() => submitRef?.setFocus()}
              onLeft={() => fmt1Ref?.setFocus()}
            />
          </Show>

          {/* Orbix Code */}
          <Show when={selectedFormat() === 2}>
            <Text x={0} y={128} style={{ fontSize: 19, color: hexColor("#8b949e"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>
              {t("orbix_code_req")}
            </Text>
            <InputField ref={(el) => { codeRef = el; }} y={160}
              placeholder="e.g. 291256" value={codeVal()}
              onEnter={() => openKb("code", "Orbix Host Code", codeRef)}
              onUp={() => nameRef?.setFocus()}
              onDown={() => usernameRef?.setFocus()}
              onLeft={() => fmt2Ref?.setFocus()}
            />
            <Text x={0} y={256} style={{ fontSize: 19, color: hexColor("#8b949e"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>
              {t("username_req")}
            </Text>
            <InputField ref={(el) => { usernameRef = el; }} y={288}
              placeholder={t("username_placeholder")} value={usernameVal()}
              onEnter={() => openKb("username", "Username", usernameRef)}
              onUp={() => codeRef?.setFocus()}
              onDown={() => passwordRef?.setFocus()}
              onLeft={() => fmt2Ref?.setFocus()}
            />
            <Text x={0} y={384} style={{ fontSize: 19, color: hexColor("#8b949e"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>
              {t("password_req")}
            </Text>
            <InputField ref={(el) => { passwordRef = el; }} y={416}
              placeholder={t("password_placeholder")}
              value={passwordVal() ? "•".repeat(passwordVal().length) : ""}
              onEnter={() => openKb("password", "Password", passwordRef)}
              onUp={() => usernameRef?.setFocus()}
              onDown={() => submitRef?.setFocus()}
              onLeft={() => fmt2Ref?.setFocus()}
            />
          </Show>

          {/* List Code */}
          <Show when={selectedFormat() === 3}>
            <Text x={0} y={0} style={{ fontSize: 19, color: hexColor("#8b949e"), fontWeight: isRTL() ? "normal" : "bold", fontFamily: fontFamily() }}>
              {t("list_code_req")}
            </Text>
            <InputField ref={(el) => { codeRef = el; }} y={32}
              placeholder="e.g. 43562268" value={codeVal()}
              onEnter={() => openKb("code", "List Code", codeRef)}
              //onUp={() => nameRef?.setFocus()}
              onDown={() => submitRef?.setFocus()}
              onLeft={() => fmt3Ref?.setFocus()}
            />
          </Show>

          {/* Validation / API errors — fade + slide-up animation */}
          <View
            x={0}
            y={submitErrors().length > 0 ? 530 : 548}
            alpha={submitErrors().length > 0 ? 1 : 0}
            width={900}
            style={{
              transition: {
                alpha: { duration: 350 },
                y: { duration: 350 },
              },
            }}
          >
            <For each={submitErrors()}>
              {(msg, i) => (
                <View x={0} y={i() * 42} width={900} height={36} borderRadius={8} color={hexColor("#3b1a1a")}>
                  <Text x={16} y={9} style={{ fontSize: 18, color: hexColor("#f87171"), fontFamily: fontFamily() }}>
                    • {msg}
                  </Text>
                </View>
              )}
            </For>
          </View>

          <SubmitButton ref={(el) => { submitRef = el; }} y={700}
            submitting={submitting()}
            onUp={() => lastField()?.setFocus()}
            onLeft={() => fmtRef(selectedFormat())?.setFocus()}
            onEnter={handleSubmit}
          />
        </View>

      </View>
    </View>
  );
};

export default AddPlaylist;
